import { useCallback, useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'

interface Props {
  latitude: number
  longitude: number
  // Lot boundary as [lng, lat] coordinate pairs (the same shape we
  // serialise on the server). When present we draw a brand-styled
  // overlay so the user can see exactly which polygon the feasibility
  // ran against.
  polygon?: number[][]
}

export function MapView({ latitude, longitude, polygon }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  // Cache the fitBounds payload so the "Reset view" button can replay
  // the exact framing the map opened with, even after the user pans
  // and zooms around.
  const initialFrameRef = useRef<{ bounds: maplibregl.LngLatBoundsLike; center: [number, number]; zoom: number } | null>(null)

  const resetView = useCallback(() => {
    const map = mapRef.current
    const frame = initialFrameRef.current
    if (!map || !frame) return
    if (frame.bounds) {
      map.fitBounds(frame.bounds, { padding: 36, animate: true, duration: 400, maxZoom: 19 })
    } else {
      map.easeTo({ center: frame.center, zoom: frame.zoom, duration: 400 })
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          'nsw-imagery': {
            type: 'raster',
            tiles: [
              'https://maps.six.nsw.gov.au/arcgis/rest/services/public/NSW_Imagery/MapServer/tile/{z}/{y}/{x}',
            ],
            tileSize: 256,
            attribution: '© NSW Department of Customer Service (Spatial Services)',
          },
        },
        layers: [{ id: 'imagery', type: 'raster', source: 'nsw-imagery' }],
      },
      center: [longitude, latitude],
      zoom: 18,
    })
    mapRef.current = map

    // Built-in zoom in / zoom out / compass control. Sit it top-right
    // so it doesn't collide with the custom reset button below.
    map.addControl(
      new maplibregl.NavigationControl({ visualizePitch: false, showCompass: true }),
      'top-right',
    )

    map.on('load', () => {
      const ring: [number, number][] | null =
        polygon && polygon.length >= 3
          ? (polygon.map(([x, y]) => [Number(x), Number(y)]) as [number, number][])
          : null

      if (ring) {
        // Close the ring if the cadastre didn't.
        const closed: [number, number][] =
          ring[0][0] === ring[ring.length - 1][0] &&
          ring[0][1] === ring[ring.length - 1][1]
            ? ring
            : [...ring, ring[0]]
        map.addSource('lot', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [closed] },
            properties: {},
          },
        })
        // Soft brand-indigo fill — keeps the aerial photo readable while
        // making the lot unambiguous at a glance.
        map.addLayer({
          id: 'lot-fill',
          type: 'fill',
          source: 'lot',
          paint: { 'fill-color': '#5b4dff', 'fill-opacity': 0.18 },
        })
        // Two-layer stroke: a wide cream "halo" below a thin indigo
        // outline reads cleanly against busy aerial tiles.
        map.addLayer({
          id: 'lot-outline-halo',
          type: 'line',
          source: 'lot',
          paint: {
            'line-color': '#f5f6fb',
            'line-width': 5,
            'line-opacity': 0.85,
          },
        })
        map.addLayer({
          id: 'lot-outline',
          type: 'line',
          source: 'lot',
          layout: { 'line-join': 'round' },
          paint: { 'line-color': '#5b4dff', 'line-width': 2 },
        })

        // Frame the lot rather than relying on the geocoded centre —
        // particularly helpful for irregular / battle-axe lots where
        // the centroid sits well off-screen at zoom 18.
        const lngs = closed.map((p) => p[0])
        const lats = closed.map((p) => p[1])
        const bounds = new maplibregl.LngLatBounds(
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        )
        initialFrameRef.current = { bounds, center: [longitude, latitude], zoom: 18 }
        map.fitBounds(bounds, { padding: 36, animate: false, maxZoom: 19 })
      } else {
        initialFrameRef.current = { bounds: undefined as unknown as maplibregl.LngLatBoundsLike, center: [longitude, latitude], zoom: 18 }
      }

      // Minimal pin at the geocoded address. SVG teardrop drawn in the
      // brand palette — matches the PDF cover for visual continuity.
      const el = document.createElement('div')
      el.style.width = '0'
      el.style.height = '0'
      el.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="30" viewBox="-12 -28 24 30">
          <path d="M0,2 C-9,2 -9,-9 0,-22 C9,-9 9,2 0,2 Z"
                fill="#5b4dff" stroke="#f5f6fb" stroke-width="1.6"
                stroke-linejoin="round"/>
          <circle cx="0" cy="-13" r="3" fill="#f5f6fb"/>
        </svg>`
      new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([longitude, latitude])
        .addTo(map)
    })

    return () => { map.remove() }
  }, [latitude, longitude, polygon])

  return (
    <div className="relative w-full h-72 border border-massing-rule overflow-hidden rounded-md">
      <div ref={containerRef} className="absolute inset-0" />
      {/* Reset-view button — sits bottom-right so it doesn't collide
          with maplibre's NavigationControl in the top-right. Glass
          surface so it reads against any aerial colour underneath. */}
      <button
        type="button"
        onClick={resetView}
        title="Reset view"
        aria-label="Reset view to lot"
        className="absolute bottom-3 right-3 z-10 px-3 py-1.5 text-[11px] uppercase tracking-wide font-medium rounded-md backdrop-blur-md surface-shade-deep hover:opacity-90 text-massing-ink border border-massing-rule transition-colors flex items-center gap-1.5"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
        Reset view
      </button>
    </div>
  )
}
